import { useEffect, useMemo, useState } from "react";
import { Loader2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

const STATUSES = ["New", "Contacted", "Quoted", "Booked", "Won", "Lost"];

const STATUS_CLASS = {
  New: "bg-terra/15 text-terra-dark border-terra/30",
  Contacted: "bg-blue-50 text-blue-700 border-blue-200",
  Quoted: "bg-violet-50 text-violet-700 border-violet-200",
  Booked: "bg-amber-50 text-amber-700 border-amber-200",
  Won: "bg-forest-100 text-forest-700 border-forest-300",
  Lost: "bg-red-50 text-red-600 border-red-200",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/leads").then((d) => setLeads(d.data)).catch((e) => {
    toast.error(formatApiErrorDetail(e.response?.data?.detail));
    setLeads([]);
  });

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!leads) return [];
    return leads.filter((l) => {
      if (typeFilter !== "all" && l.type !== typeFilter) return false;
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      return true;
    });
  }, [leads, typeFilter, statusFilter]);

  const patch = async (lead, body) => {
    try {
      const { data } = await api.patch(`/admin/leads/${lead.type}/${lead.id}`, body);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...data } : l)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openDetail = (lead) => {
    setDetail(lead);
    setNotes(lead.notes || "");
  };

  const saveDetail = async () => {
    if (!detail) return;
    setSaving(true);
    await patch(detail, { notes });
    setSaving(false);
    setDetail(null);
    toast.success("Lead updated");
  };

  return (
    <div data-testid="admin-leads">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-9 w-44 border-forest-700/20 bg-white" data-testid="lead-filter-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All lead types</SelectItem>
            <SelectItem value="quote">Quote requests</SelectItem>
            <SelectItem value="inspection">Inspection bookings</SelectItem>
            <SelectItem value="signup">Interest signups</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-40 border-forest-700/20 bg-white" data-testid="lead-filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs font-bold text-moss" data-testid="leads-count">
          {leads === null ? "Loading…" : `${filtered.length} lead${filtered.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {leads === null ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-forest-700" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-forest-700/20 bg-white px-6 py-16 text-center" data-testid="leads-empty">
          <p className="font-display text-lg font-bold text-forest-700">No leads here yet.</p>
          <p className="mt-1 text-sm text-moss">Quote requests, inspection bookings and interest signups will appear in this inbox as they come in.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-forest-700/10 bg-white">
          <Table data-testid="leads-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-36">Received</TableHead>
                <TableHead className="w-28">Type</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Details</TableHead>
                <TableHead className="w-36">Status</TableHead>
                <TableHead className="w-24">Email</TableHead>
                <TableHead className="w-20">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((lead) => (
                <TableRow key={`${lead.type}-${lead.id}`} data-testid={`lead-row-${lead.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-moss">{fmtDate(lead.created_at)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={lead.type === "quote" ? "border-terra/40 bg-terra/10 text-terra-dark" : lead.type === "inspection" ? "border-forest-700/25 bg-forest-50 text-forest-700" : "border-wave/40 bg-blue-50 text-blue-700"}>
                      {lead.type === "quote" ? "Quote" : lead.type === "inspection" ? "Inspection" : "Signup"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-semibold text-forest-700">{lead.name}</TableCell>
                  <TableCell className="text-xs text-moss">
                    <div>{lead.phone}</div>
                    <div className="text-moss/70">{lead.email}</div>
                  </TableCell>
                  <TableCell className="max-w-56 text-xs text-moss">
                    {lead.type === "quote"
                      ? [lead.suburb && `${lead.suburb} ${lead.postcode}`, lead.product_interest].filter(Boolean).join(" · ") || "—"
                      : lead.type === "signup"
                        ? lead.topic || "—"
                        : [lead.address, lead.preferred_date, lead.time_window].filter(Boolean).join(" · ") || "—"}
                  </TableCell>
                  <TableCell>
                    <Select value={lead.status} onValueChange={(v) => patch(lead, { status: v })}>
                      <SelectTrigger className={`h-8 w-full border text-xs font-bold ${STATUS_CLASS[lead.status] || ""}`} data-testid={`lead-status-select-${lead.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-xs font-bold ${lead.email_status === "sent" ? "text-forest-600" : lead.email_status === "failed" ? "text-red-500" : "text-moss/50"}`}
                      data-testid={`lead-email-status-${lead.id}`}
                    >
                      {lead.email_status === "sent" ? "Sent" : lead.email_status === "failed" ? "Failed" : "…"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" className="h-8 gap-1.5 border-forest-700/20" onClick={() => openDetail(lead)} data-testid={`lead-notes-button-${lead.id}`}>
                      <StickyNote className="h-3.5 w-3.5" /> {lead.notes ? "Edit" : "Add"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-forest-700">
              {detail?.type === "quote" ? "Quote request" : detail?.type === "signup" ? "Interest signup" : "Inspection booking"} — {detail?.name}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <dl className="grid grid-cols-[110px_1fr] gap-x-4 gap-y-2 text-sm">
                {[
                  ...(detail.type !== "signup" ? [["Phone", detail.phone]] : []),
                  ["Email", detail.email],
                  ...(detail.type === "quote"
                    ? [
                        ["Suburb", detail.suburb || "—"],
                        ["Postcode", detail.postcode || "—"],
                        ["Interest", detail.product_interest || "—"],
                      ]
                    : detail.type === "signup"
                      ? [["Interested in", detail.topic || "—"]]
                      : [
                          ["Address", detail.address || "—"],
                          ["Preferred date", detail.preferred_date || "—"],
                          ["Time window", detail.time_window || "—"],
                          ["System", detail.system_interest || "—"],
                          ["Consent to contact", detail.consent
                            ? `Yes — given ${fmtDate(detail.consent_at || detail.created_at)}`
                            : "Not recorded"],
                        ]),
                  ["Message", detail.message || "—"],
                  ["Received", fmtDate(detail.created_at)],
                ].map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-xs font-bold uppercase tracking-[0.1em] text-moss">{k}</dt>
                    <dd className="text-forest-700">{v}</dd>
                  </div>
                ))}
              </dl>
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700">Notes</label>
                <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Called 2pm, wants battery added next year…" data-testid="lead-notes-input" />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setDetail(null)} data-testid="lead-detail-cancel">Cancel</Button>
                <Button onClick={saveDetail} disabled={saving} className="gap-2 rounded-full bg-forest-700 hover:bg-forest-600" data-testid="lead-detail-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
